import React, { useState, useEffect, useRef } from "react";
import "./Chatbot.css";
import botIcon from "./bot.png";
import axios from "axios";
import BASE_URL from "../../config/apiConfig";
import { marked } from "marked";

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hello! I'm Farmology AI 🌱. How can I assist your farm today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const isRecognitionActiveRef = useRef(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const animationRef = useRef(null);
  const recordingTimerRef = useRef(null);
const [playingIndex, setPlayingIndex] = useState(null);

  // Initialize speech recognition
  useEffect(() => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      setSpeechSupported(true);
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();

      recognitionRef.current.continuous = true; // stop after pause
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-IN";

      recognitionRef.current.onresult = (event) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        stopListening(true);
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: "Speech recognition error: " + event.error },
        ]);
      };

      recognitionRef.current.onend = () => {
        isRecognitionActiveRef.current = false;
        stopAudioVisualization();
        setIsListening(false);
      };

      recognitionRef.current.onstart = () => {
        isRecognitionActiveRef.current = true;
        setRecordingTime(0);
        startRecordingTimer();
      };
    }

    return () => {
      stopListening(true);
    };
  }, []);

  // Microphone check
  const checkMicrophone = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Microphone not accessible." },
      ]);
      return false;
    }
  };

  // Timer
  const startRecordingTimer = () => {
    stopRecordingTimer();
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  const stopRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingTime(0);
  };

  // Audio visualization
  const startAudioVisualization = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();

      const microphone =
        audioContextRef.current.createMediaStreamSource(stream);
      microphone.connect(analyserRef.current);

      analyserRef.current.fftSize = 256;
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateAudioLevel = () => {
        if (isListening) {
          analyserRef.current.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            sum += dataArray[i];
          }
          const average = sum / bufferLength;
          setAudioLevel(Math.min(100, (average / 255) * 100));
          animationRef.current = requestAnimationFrame(updateAudioLevel);
        }
      };
      updateAudioLevel();
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopAudioVisualization = () => {
    setAudioLevel(0);
    stopRecordingTimer();
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing]);

  // Start listening
  const startListening = async () => {
    if (recognitionRef.current && speechSupported) {
      const micOk = await checkMicrophone();
      if (!micOk) return;

      if (isRecognitionActiveRef.current) return;

      try {
        setIsListening(true);
        setInput("");
        await startAudioVisualization();
        recognitionRef.current.start();
        isRecognitionActiveRef.current = true;
      } catch (err) {
        console.error("Error starting recognition:", err.message);
      }
    }
  };

  // Stop listening
  const stopListening = (forceStop = false) => {
    if (recognitionRef.current && isRecognitionActiveRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        console.warn("Stop error:", err.message);
      }
      isRecognitionActiveRef.current = false;
    }
    setIsListening(false);
    stopAudioVisualization();
    if (forceStop) stopRecordingTimer();
  };

  // Send message
  const sendMessage = async (messageOverride = null) => {
    const userMessage = messageOverride || input;
    if (!userMessage.trim()) return;

    // Always stop mic when sending
    stopListening(true);

    setMessages((prev) => [...prev, { sender: "user", text: userMessage }]);
    setInput("");
    setTyping(true);

    try {
      const response = await axios.post(`${BASE_URL}/api/user/chatbot_audio/`, {
        message: userMessage,
      });

      setTyping(false);

      if (response.data.reply) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: response.data.reply,
            audio: response.data.audio, // 👈 store audio too
          },
        ]);
      }
    } catch (error) {
      setTyping(false);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Sorry, something went wrong." },
      ]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isListening) sendMessage();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatBotMessage = (text) => {
    // Split by double newlines for paragraphs
    return text.split("\n\n").map((para, idx) => <p key={idx}>{para}</p>);
  };

  const getVoices = () => {
    return new Promise((resolve) => {
      let voices = window.speechSynthesis.getVoices();
      if (voices.length) resolve(voices);
      else {
        // Some browsers load voices asynchronously
        window.speechSynthesis.onvoiceschanged = () => {
          voices = window.speechSynthesis.getVoices();
          resolve(voices);
        };
      }
    });
  };

  // const speakMessage = async (audioBase64) => {
  //   try {
  //     if (!audioBase64) return;
  //     const audioBytes = Uint8Array.from(atob(audioBase64), (c) =>
  //       c.charCodeAt(0)
  //     );
  //     const audioBlob = new Blob([audioBytes], { type: "audio/mp3" });
  //     const audioUrl = URL.createObjectURL(audioBlob);
  //     const audio = new Audio(audioUrl);

  //     // Add error handling for audio playback
  //     audio.oncanplay = () => {
  //       audio.play().catch((err) => console.error("Audio play failed:", err));
  //     };

  //     // Clean up the object URL after playing
  //     audio.onended = () => {
  //       URL.revokeObjectURL(audioUrl);
  //     };
  //   } catch (err) {
  //     console.error("Audio play error:", err);
  //   }
  // };

  // const speakMessage = async (text) => {
  //   if (!window.speechSynthesis) return;

  //   window.speechSynthesis.cancel();

  //   const utterance = new SpeechSynthesisUtterance(text);

  //   // Detect language
  //   const isHindi = /[\u0900-\u097F]/.test(text);
  //   const isBengali = /[\u0980-\u09FF]/.test(text);
  //   const isKannada = /[\u0C80-\u0CFF]/.test(text);

  //   let voices = await getVoices();

  //   // Pick a natural-sounding voice
  //   let voice;
  //   if (isHindi) voice = voices.find((v) => v.lang === "hi-IN") || voices[0];
  //   else if (isBengali)
  //     voice = voices.find((v) => v.lang === "bn-IN") || voices[0];
  //   else if (isKannada)
  //     voice = voices.find((v) => v.lang === "kn-IN") || voices[0];
  //   else voice = voices.find((v) => v.lang.startsWith("en")) || voices[0];

  //   utterance.voice = voice;
  //   utterance.rate = 0.6; // 0.9–1.2 usually sounds more natural
  //   utterance.pitch = 3.05; // slightly higher pitch feels human

  //   window.speechSynthesis.speak(utterance);
  // };

  const renderBotMessage = (text) => {
    // Convert Markdown to HTML
    const html = marked.parse(text);

    // Return as React element
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // Create a ref to track all active audios
  const activeAudiosRef = useRef({});

  const speakMessage = async (audioBase64, messageId) => {
    try {
      if (!audioBase64) return;

      // If the same message is already playing → stop it
      if (activeAudiosRef.current[messageId]) {
        activeAudiosRef.current[messageId].pause();
        activeAudiosRef.current[messageId].currentTime = 0;
        delete activeAudiosRef.current[messageId];
        setPlayingIndex(null);
        return;
      }

      // Decode base64 to audio
      const audioBytes = Uint8Array.from(atob(audioBase64), (c) =>
        c.charCodeAt(0)
      );
      const audioBlob = new Blob([audioBytes], { type: "audio/mp3" });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      // Track audio instance
      activeAudiosRef.current[messageId] = audio;
      setPlayingIndex(messageId);

      // Play audio
      await audio.play();

      // Clean up after playback
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        delete activeAudiosRef.current[messageId];
        setPlayingIndex(null);
      };
    } catch (err) {
      console.error("Audio play error:", err);
      setPlayingIndex(null);
    }
  };

const renderMessage = (msg, index) => (
  <div key={index} className={`message ${msg.sender}`}>
    {msg.sender === "bot" ? (
      <div className="bot-message-with-play">
        {renderBotMessage(msg.text)}
        {msg.audio && (
          <button
            className={`play-button ${playingIndex === index ? "playing" : ""}`}
            onClick={() => speakMessage(msg.audio, index)}
            title={playingIndex === index ? "Stop playback" : "Play response"}
          >
            {playingIndex === index ? "🔇" : "🔊"}
          </button>
        )}
      </div>
    ) : (
      <span>{msg.text}</span>
    )}
  </div>
);




  const markdownToHTML = (markdown) => {
    const lines = markdown.split("\n");
    let html = "<table>";
    lines.forEach((line) => {
      if (line.trim() === "") return;
      const cols = line.split("|").filter(Boolean);
      html += "<tr>";
      cols.forEach((col) => (html += `<td>${col.trim()}</td>`));
      html += "</tr>";
    });
    html += "</table>";
    return html;
  };

  return (
    <div id="chatbot-container">
      <div id="chatbot-icon" onClick={() => setOpen(!open)}>
        <img src={botIcon} alt="Farmology Bot" />
      </div>

      {open && (
        <div id="chatbox">
          <div id="chatbox-header">
            <span>Farmology AI 🌱</span>
            <span id="chatbox-close" onClick={() => setOpen(false)}>
              &times;
            </span>
          </div>

          <div id="chatbox-body">
            <div id="chat-messages">
              {messages.map(renderMessage)}

              {typing && (
                <div className="message bot typing">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              )}

              {isListening && (
                <div className="message bot listening-recorder">
                  <div className="recorder-header">
                    <div className="recording-indicator">
                      <div className="recording-dot"></div>
                      <span>Recording...</span>
                    </div>
                    <div className="recording-timer">
                      {formatTime(recordingTime)}
                    </div>
                  </div>

                  <div className="audio-visualizer">
                    <div className="waveform-container">
                      {[...Array(20)].map((_, i) => (
                        <div
                          key={i}
                          className="waveform-bar"
                          style={{
                            height: `${Math.max(
                              2,
                              audioLevel * Math.random() * 0.8 +
                                audioLevel * 0.2
                            )}%`,
                            animationDelay: `${i * 0.05}s`,
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef}></div>
            </div>

            <div className="chat-input-container">
              <div className="input-wrapper">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isListening
                      ? "Listening... speak now"
                      : "Type your question or click mic to speak..."
                  }
                />

                {speechSupported && (
                  <button
                    className={`mic-button ${isListening ? "listening" : ""}`}
                    onClick={
                      isListening ? () => stopListening(true) : startListening
                    }
                    title={isListening ? "Stop listening" : "Start voice input"}
                  >
                    {isListening ? (
                      <div className="mic-listening-icon">
                        <div className="sound-waves">
                          <div className="wave-1"></div>
                          <div className="wave-2"></div>
                          <div className="wave-3"></div>
                        </div>
                      </div>
                    ) : (
                      "🎤"
                    )}
                  </button>
                )}
              </div>

              <button
                className="send-button"
                onClick={() => sendMessage()}
                disabled={!input.trim()}
              >
                Send
              </button>
            </div>

            {!speechSupported && (
              <div className="speech-not-supported">
                Voice input not supported in this browser. Please use Chrome,
                Edge, or Safari.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
